import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Target, Plus, Home, UserPlus, BedDouble, Layers, Users2, Inbox, Handshake, ArrowRight, Flame } from "lucide-react";
import { PageShell } from "@/components/Navbar";
import { PageBanner, PAGE_BANNERS } from "@/components/PageBanner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MyPropertiesPanel } from "@/components/MyPropertiesPanel";
import { MyBuyersPanel } from "@/components/MyBuyersPanel";
import { OpportunityFeed } from "@/components/OpportunityFeed";

function OverviewCard({ testid, icon: Icon, title, stats, actions, nav }) {
  return (
    <div data-testid={testid} className="bg-card border border-border rounded-2xl p-5 flex flex-col">
      <div className="flex items-center gap-2.5">
        <div className="h-10 w-10 rounded-xl bg-[#0F2C59]/6 text-[#0F2C59] flex items-center justify-center"><Icon className="h-5 w-5" /></div>
        <h3 className="font-display text-base font-semibold text-slate-900">{title}</h3>
      </div>
      <div className="flex gap-6 mt-4">
        {stats.map((s) => (
          <div key={s.label}>
            <div className="text-2xl font-bold text-slate-900 tabular-nums" data-testid={s.testid}>{s.value}</div>
            <div className="text-[11px] uppercase tracking-wide text-slate-400 font-medium mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-slate-100">
        {actions.map((a) => (
          <button key={a.label} data-testid={a.testid} onClick={() => nav(a.to)} className={`inline-flex items-center gap-1.5 text-sm font-semibold rounded-lg px-3 py-1.5 transition-colors ${a.primary ? "bg-accent text-accent-foreground hover:bg-accent/90" : "text-primary hover:bg-primary/5"}`}>
            {a.label}{a.primary ? <Plus className="h-3.5 w-3.5" /> : <ArrowRight className="h-3.5 w-3.5" />}
          </button>
        ))}
      </div>
    </div>
  );
}

// Opportunities: a genuine working module that surfaces the professional's own
// Properties and Buyer Needs (with full management functionality) via shared
// reusable panels — no duplicated business logic, no nested PageShell/Navbar.
export default function Opportunities() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [collabRequests, setCollabRequests] = useState(0);

  useEffect(() => {
    // One cached deterministic call for orientation counts (no duplicate Match Engine run).
    api.get("/dashboard/opportunities").then((r) => setKpis(r.data.kpis)).catch(() => setKpis(null));
    api.get("/stats").then((r) => setCollabRequests(r.data.my_collaboration_requests || 0)).catch(() => {});
  }, []);
  const n = (v) => (kpis == null ? "—" : (v ?? 0));

  const canHost = user && (user.role === "ADMIN" || (
    (user.account_status || "ACTIVE") === "ACTIVE"
    && user.verification_status === "PROFESSIONAL_VERIFIED"
    && ["PROPERTY_MANAGER", "LICENSED_BROKER"].includes(user.role)
  ));
  const [sp, setSp] = useSearchParams();
  const _tabParam = sp.get("tab");
  const initial = _tabParam === "buyer-needs" ? "buyer-needs"
    : _tabParam === "properties" ? "properties"
    : "requires-attention";
  const [tab, setTab] = useState(initial);

  const onTab = (v) => {
    setTab(v);
    setSp(v === "requires-attention" ? {} : { tab: v }, { replace: true });
  };

  return (
    <PageShell testid="opportunities-page">
      <PageBanner
        testid="opportunities-banner"
        image={PAGE_BANNERS.opportunities}
        icon={Target}
        title="My Opportunities"
        description="Your working pipeline — properties, buyer needs, inquiries and collaborations."
        actions={
          <>
            <Button data-testid="opp-add-property" onClick={() => nav("/have-property")} className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-xl font-semibold"><Plus className="h-4 w-4 mr-1.5" /> Add Property</Button>
            {canHost && <Button data-testid="opp-list-a-stay" onClick={() => nav("/have-property?type=Unit+Hosting")} variant="outline" className="rounded-xl font-semibold border-emerald-600/40 text-emerald-700 hover:bg-emerald-50"><BedDouble className="h-4 w-4 mr-1.5" /> List a Stay</Button>}
            <Button data-testid="opp-post-buyer-need" onClick={() => nav("/have-buyer")} variant="outline" className="rounded-xl font-semibold border-primary/30 text-primary hover:bg-primary/5"><Plus className="h-4 w-4 mr-1.5" /> Post Buyer Need</Button>
          </>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8" data-testid="opp-overview">
        <OverviewCard testid="ov-properties" icon={Layers} title="Properties" nav={nav}
          stats={[{ label: "Active", value: n(kpis?.active_properties), testid: "ov-active-properties" }, { label: "New Matches", value: n(kpis?.new_matches), testid: "ov-new-matches" }]}
          actions={[{ label: "Manage", to: "/my-properties", testid: "ov-manage-properties" }, { label: "Add Property", to: "/have-property", primary: true, testid: "ov-add-property" }]} />
        <OverviewCard testid="ov-buyer-needs" icon={Users2} title="Buyer Needs" nav={nav}
          stats={[{ label: "Active", value: n(kpis?.active_buyer_needs), testid: "ov-active-buyer-needs" }]}
          actions={[{ label: "Manage", to: "/my-buyers", testid: "ov-manage-buyers" }, { label: "Post Need", to: "/have-buyer", primary: true, testid: "ov-post-buyer-need" }]} />
        <OverviewCard testid="ov-inquiries" icon={Inbox} title="Inquiries" nav={nav}
          stats={[{ label: "New", value: n(kpis?.new_inquiries), testid: "ov-new-inquiries" }]}
          actions={[{ label: "View Inquiries", to: "/inquiries", testid: "ov-view-inquiries" }]} />
        <OverviewCard testid="ov-collaborations" icon={Handshake} title="Collaborations" nav={nav}
          stats={[{ label: "Requests", value: kpis == null ? "—" : collabRequests, testid: "ov-collab-requests" }, { label: "Active", value: n(kpis?.active_collaborations), testid: "ov-active-collaborations" }]}
          actions={[{ label: "View Collaborations", to: "/collaborations", testid: "ov-view-collaborations" }]} />
      </div>

      <Tabs value={tab} onValueChange={onTab} className="w-full">
        <TabsList data-testid="opp-tabs" className="mb-6 max-sm:h-auto max-sm:w-full max-sm:flex-wrap max-sm:justify-start max-sm:gap-1">
          <TabsTrigger data-testid="opp-tab-requires-attention" value="requires-attention" className="gap-2"><Flame className="h-4 w-4" /> Requires Attention</TabsTrigger>
          <TabsTrigger data-testid="opp-tab-properties" value="properties" className="gap-2"><Home className="h-4 w-4" /> Properties</TabsTrigger>
          <TabsTrigger data-testid="opp-tab-buyer-needs" value="buyer-needs" className="gap-2"><UserPlus className="h-4 w-4" /> Buyer Needs</TabsTrigger>
        </TabsList>
        <TabsContent value="requires-attention" data-testid="opp-panel-requires-attention">
          <div className="mb-4">
            <h2 className="font-display text-xl font-semibold tracking-tight text-slate-900">Opportunities Requiring Attention</h2>
            <p className="text-sm text-slate-500 mt-0.5">Your highest-priority matches, requests, inquiries and follow-ups.</p>
          </div>
          <OpportunityFeed />
        </TabsContent>
        <TabsContent value="properties" data-testid="opp-panel-properties">
          <MyPropertiesPanel embedded />
        </TabsContent>
        <TabsContent value="buyer-needs" data-testid="opp-panel-buyer-needs">
          <MyBuyersPanel embedded />
        </TabsContent>
      </Tabs>
    </PageShell>
  );
}
